import { Asterisk } from 'components/design-system/form/Asterisk'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { useBreakoutUser } from 'hooks/profile'
import { observer } from 'mobx-react-lite'
import { useTranslationTyped } from 'i18n/i18n'
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useRepository } from 'hooks/auth'
import { useCallback, useEffect, useMemo } from 'react'
import { inProduction } from 'config/environment'
import { BreakoutCheckbox } from 'components/design-system/BreakoutCheckbox'

type FormData = {
  firstName: string
  lastName: string
  emailAddress: string
  company: string | undefined
  onboardComplete?: boolean
  walkthroughComplete?: boolean
}
export const ProfileInfoSection = observer(function ProfileInfoSection() {
  const { tt } = useTranslationTyped()
  const breakoutUser = useBreakoutUser()
  const { featureFlags } = useRepository()

  const allowEmailUpdate = featureFlags.data.allowEmailUpdate

  const showCompanyInput = useMemo(() => {
    return breakoutUser.isInstructor || breakoutUser.isCorre
  }, [breakoutUser.isInstructor, breakoutUser.isCorre])

  const showWalkthroughInput = !inProduction()

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
    setFocus,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      firstName: breakoutUser.firstName,
      lastName: breakoutUser.lastName,
      emailAddress: breakoutUser.emailAddress,
      company: breakoutUser.profile.data.company || undefined,
      onboardComplete: breakoutUser.profile.data.onboardComplete ?? false,
      walkthroughComplete:
        breakoutUser.profile.data.walkthroughComplete ?? false,
    },
  })

  useEffect(() => {
    // Reset the form emailAddress form field when the user email changes.
    reset({
      emailAddress: breakoutUser.emailAddress,
    })
  }, [breakoutUser.emailAddress, reset])

  const onError: SubmitErrorHandler<FormData> = useCallback(
    (errors) => {
      const firstErrorField = (
        ['firstName', 'lastName', 'emailAddress', 'company'] as const
      ).find((field) => errors[field])
      if (firstErrorField) {
        setFocus(firstErrorField)
      }
    },
    [setFocus]
  )

  const onSubmit: SubmitHandler<FormData> = async ({
    firstName,
    lastName,
    emailAddress,
    company,
    onboardComplete,
    walkthroughComplete,
  }) => {
    const updateProfileFromFormData = async () => {
      const promises: Promise<unknown>[] = []

      // did name update
      if (
        firstName !== breakoutUser.firstName ||
        lastName !== breakoutUser.lastName
      ) {
        promises.push(
          breakoutUser.updateName(firstName.trim(), lastName.trim())
        )
      }

      // did hubspot company update
      if (company && company !== breakoutUser.profile.data.company) {
        promises.push(breakoutUser.updateCompany(company.trim()))
      }

      // should we update walkthrough
      if (showWalkthroughInput) {
        if (
          onboardComplete !== undefined &&
          onboardComplete !== breakoutUser.profile.data.onboardComplete
        )
          promises.push(breakoutUser.setOnboardingComplete(onboardComplete))

        if (
          walkthroughComplete !== undefined &&
          walkthroughComplete !== breakoutUser.profile.data.walkthroughComplete
        )
          promises.push(
            breakoutUser.setWalkthroughComplete(walkthroughComplete)
          )
      }

      await Promise.all(promises)

      // display success message if any updates were made
      if (promises.length)
        toast.success(tt.profile.profile_updated(), {
          id: 'profile-updated',
        })
    }

    // if email did not change, update the name only
    if (!allowEmailUpdate || emailAddress === breakoutUser.emailAddress) {
      return await updateProfileFromFormData()
    }

    const [verified, errKey] =
      await breakoutUser.profile.startEmailUpdateVerification(emailAddress)

    if (!verified) {
      toast.error(tt.profile[errKey]())
      return
    }

    await updateProfileFromFormData()

    toast.success(tt.profile.email_verification_sent(), {
      duration: 12000,
    })
    return
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex h-full flex-col justify-between"
    >
      <div>
        <h2 className="text-headline-large text-core-on-tertiary">
          {tt.profile.details_headline()}
        </h2>
        <p className="mt-1 text-body-medium text-on-surface-var">
          <Asterisk ariaHidden />
          {' ' + tt.profile.required_field_indicator()}
        </p>

        <div className="mt-6 flex flex-col gap-3">
          <Controller
            control={control}
            name="firstName"
            rules={{
              required: tt.profile.first_name_required(),
            }}
            render={({ field, fieldState }) => (
              <BreakoutTextInput
                required
                kind="secondary"
                label={tt.profile.first_name()}
                data-testid="firstNameInput"
                autoComplete="given-name"
                error={fieldState.error}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="lastName"
            rules={{
              required: tt.profile.last_name_required(),
            }}
            render={({ field, fieldState }) => (
              <BreakoutTextInput
                required
                kind="secondary"
                label={tt.profile.last_name()}
                data-testid="lastNameInput"
                autoComplete="family-name"
                error={fieldState.error}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="emailAddress"
            rules={{
              required: tt.profile.email_required(),
            }}
            render={({ field, fieldState }) => (
              <BreakoutTextInput
                required
                disabled={!allowEmailUpdate}
                kind="secondary"
                label={tt.profile.email()}
                data-testid="emailInput"
                autoComplete="email"
                error={fieldState.error}
                {...field}
              />
            )}
          />
          {showCompanyInput && (
            <Controller
              control={control}
              name="company"
              render={({ field, fieldState }) => (
                <BreakoutTextInput
                  disabled={!showCompanyInput}
                  kind="secondary"
                  label={tt.profile.institution()}
                  data-testid="companyInput"
                  autoComplete="organization"
                  error={fieldState.error}
                  required={false}
                  {...field}
                />
              )}
            />
          )}
          {showWalkthroughInput && (
            <div className="flex flex-row justify-evenly">
              <Controller
                control={control}
                name="onboardComplete"
                render={({ field }) => (
                  <BreakoutCheckbox
                    testId="onboarding-complete"
                    label={tt.profile.onboarding_complete()}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="walkthroughComplete"
                render={({ field }) => (
                  <BreakoutCheckbox
                    testId="walkthrough-complete"
                    label={tt.profile.walkthrough_complete()}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-end">
        <BreakoutButton
          data-testid="save-profile"
          type="submit"
          size="large"
          kind="primary"
          className="mt-3"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {tt.profile.save()}
        </BreakoutButton>
      </div>
    </form>
  )
})
